import { Flex, ImagePlaceholder } from '@solinkcorp/solink-components';
import classNames from 'classnames';
import { type IEvent } from 'clients/PlatformApi';
import React, { type CSSProperties, useCallback } from 'react';
import styles from './S3Image.module.sass';
import { toCssDimension, useS3Thumbnails } from './useS3Thumbnails';

const FORCE_ASPECT_RATIO_DEFAULT = 'containerOnly';

export interface S3ImageProps {
  /** The camera ID. */
  id: string;
  /** The URL to access camera thumbnails (e.g. `camera.thumbnail`). */
  url?: string;
  blur?: boolean;
  className?: string;
  checkInUrl?: string;
  images?: IEvent['cameras'][0]['images'];
  name?: string;
  onClick?: (id: string) => void;
  updateImageProcessingState?: (state: boolean) => void;
  /**
   * If `width` and `height` are both unspecified, the image will fill the
   * parent container (100% width and 100% height) while maintaining the image
   * aspect ratio.  If height or width are specified, the image will be scaled
   * to the specified width/height.
   */
  width?: number;
  height?: number;
  /**
   * The width of the image that we're going to display.  This is the real width
   * of the image, in pixels.  This is used to set the height and width of the
   * <img> element, so it doesn't change size when it loads.  Note that the <img>
   * element is rendered inside a <div>, and will fill the div by default.
   */
  imageWidth?: number;
  /**
   * The height of the image that we're going to display.  See `imageWidth`.
   */
  imageHeight?: number;
  /**
   * If true, this will force the image and the container the image is in into
   * a 16x9 aspect ration.  If "containerOnly" or undefined, then the image will
   * be the correct aspect ratio, but will be presented inside a 16x9 container.
   * If false, then the aspect ratio of neither the image nor the container will
   * be constraine.
   */
  forceAspectRatio?: boolean | 'containerOnly';
  style?: CSSProperties;
  loading?: 'eager' | 'lazy' | undefined;
  draggable?: boolean;
}

/**
 * Load an image stored on S3 (such as a camera thumbnail) from S3 or CloudFront.
 *
 * The thumbnail list, the fallback chain and the rotation live in `useS3Thumbnails`,
 * which the Aspen twin `AspenS3Image` also calls, so both skins resolve the same URL at
 * the same moment. This component keeps the Legacy chrome.
 */
const S3Image: React.FC<S3ImageProps> = (props) => {
  const {
    blur,
    className = '',
    name,
    id: cameraId,
    onClick: propsOnClick,
    loading,
    updateImageProcessingState,
    checkInUrl,
    imageWidth,
    imageHeight,
    // browser default for images is draggable
    draggable = true,
  } = props;

  const forceAspectRatio = props.forceAspectRatio ?? FORCE_ASPECT_RATIO_DEFAULT;
  const forceImageAspectRatio = forceAspectRatio === true;
  const forceContainerAspectRatio =
    forceAspectRatio === true || forceAspectRatio === 'containerOnly';

  const { thumbnails, currentIndex, onImageError } = useS3Thumbnails({
    url: props.url,
    checkInUrl,
    images: props.images,
    updateImageProcessingState,
  });

  const onClick = useCallback(() => {
    propsOnClick?.(cameraId);
  }, [propsOnClick, cameraId]);

  const s3ImageClass = classNames(styles['s3-image'], 's3-image', className, {
    'blur-image': blur,
    [styles['default-aspect-ratio']]: forceContainerAspectRatio,
  });

  let objectFit: CSSProperties['objectFit'];
  if (forceImageAspectRatio) {
    // Squash the image, ignoring aspect ratio.
    objectFit = 'fill';
  } else if (imageWidth && imageHeight && imageHeight > imageWidth) {
    // Portrait images we draw black bars on either side.
    objectFit = 'contain';
  } else {
    // Landscape images, we cut off a little of the top and bottom.
    objectFit = 'cover';
  }

  return (
    <Flex
      className={s3ImageClass}
      justifyContent="center"
      alignItems="center"
      width={toCssDimension(props.width)}
      height={toCssDimension(props.height)}
      style={props.style}
    >
      {thumbnails.length > 0 ? (
        thumbnails.map(({ date, url, id }, idx) => {
          return (
            <img
              className={`images-${thumbnails.length}`}
              key={`${cameraId}-${id}-thumbnail`}
              id={`${date}`}
              style={{
                display: idx === currentIndex || thumbnails.length === 1 ? `block` : `none`,
                width: toCssDimension(props.width),
                height: toCssDimension(props.height),
                objectFit,
              }}
              src={url}
              title={name}
              onClick={onClick}
              onError={() => onImageError(id)}
              width={imageWidth || 'auto'}
              height={imageHeight || 'auto'}
              loading={loading}
              draggable={draggable}
            />
          );
        })
      ) : (
        <ImagePlaceholder color="inherit" />
      )}
    </Flex>
  );
};

export default React.memo(S3Image);
